import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MapPin, Home as HomeIcon, Phone, MessageCircle, ArrowRight, CheckCircle, Star, TrendingUp, Shield, Clock } from 'lucide-react';
import { companyInfo, featuredPlots, featuredHomes, services, stats, testimonials } from '../mock';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleWhatsApp = () => {
    window.open(`https://wa.me/91${companyInfo.whatsapp}?text=Hi, I'm interested in your properties`, '_blank');
  };

  const handleCall = (phone) => {
    window.location.href = `tel:+91${phone}`;
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden">
        <div className="hero-overlay absolute inset-0"></div>
        <div className="hero-gradient absolute inset-0"></div>
        
        <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
          <div className={`hero-content text-center transition-all duration-1000 transform ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="inline-block mb-4 md:mb-6">
              <Badge className="hero-badge px-4 md:px-6 py-2 text-sm md:text-base">
                <Star className="w-3 h-3 md:w-4 md:h-4 mr-2" fill="currentColor" />
                Delhi's Trusted Property Developers
              </Badge>
            </div>
            
            <h1 className="hero-title text-4xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-6">
              <span className="gradient-text">{companyInfo.name}</span>
            </h1>
            
            <p className="hero-subtitle text-xl md:text-2xl lg:text-3xl mb-3 md:mb-4 text-orange-400 font-semibold">
              {companyInfo.slogan}
            </p>
            
            <p className="hero-description text-base md:text-xl lg:text-2xl mb-8 md:mb-12 max-w-3xl mx-auto text-gray-300 px-4">
              Premium Residential Plots & Dream Homes in Delhi's Most Sought-After Locations
            </p>
            
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center px-4">
              <Button 
                size="lg" 
                className="cta-button-primary group w-full sm:w-auto"
                onClick={() => document.getElementById('featured-properties').scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Properties
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              <Button 
                size="lg" 
                variant="outline"
                className="cta-button-secondary w-full sm:w-auto"
                onClick={handleWhatsApp}
              >
                <MessageCircle className="mr-2 w-5 h-5" />
                WhatsApp Us
              </Button>
            </div>
          </div>
        </div>

        {/* Floating Stats */}
        <div className="container mx-auto px-4 pb-12 md:pb-16 relative z-10">
          <div className="stats-grid grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, index) => (
              <div 
                key={index}
                className="stat-card text-center transform hover:scale-105 transition-all duration-300"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="text-4xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Plots Section */}
      <section id="featured-properties" className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50">
              Featured Properties
            </Badge>
            <h2 className="section-title text-5xl font-bold mb-4">
              <span className="gradient-text">Premium Residential Plots</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Carefully selected plots in prime locations with excellent connectivity and clear documentation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredPlots.map((plot, index) => (
              <Card 
                key={plot.id} 
                className="property-card group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="property-image-wrapper relative overflow-hidden">
                  <img 
                    src={plot.image} 
                    alt={plot.title}
                    className="property-image w-full h-64 object-cover"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-orange-500 text-white border-0">{plot.status}</Badge>
                  </div>
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-black/80 text-orange-400 border-orange-500/50">
                      <MapPin className="w-3 h-3 mr-1" />
                      Plot
                    </Badge>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white group-hover:text-orange-400 transition-colors">
                    {plot.title}
                  </h3>
                  
                  <div className="flex items-center text-gray-400 text-sm mb-4">
                    <MapPin className="w-4 h-4 mr-1" />
                    {plot.location}
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="plot-detail">
                      <div className="text-xs text-gray-500">Size</div>
                      <div className="text-sm font-semibold text-white">{plot.size}</div>
                    </div>
                    <div className="plot-detail">
                      <div className="text-xs text-gray-500">Dimensions</div>
                      <div className="text-sm font-semibold text-white">{plot.dimensions}</div>
                    </div>
                  </div>

                  <div className="plot-detail mb-4">
                    <div className="text-xs text-gray-500">Road Width</div>
                    <div className="text-sm font-semibold text-orange-400">{plot.roadWidth}</div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                    <div>
                      <div className="text-2xl font-bold gradient-text">{plot.price}</div>
                    </div>
                    <Link to="/properties">
                      <Button size="sm" className="cta-button-mini">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Homes Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50">
              Dream Homes
            </Badge>
            <h2 className="section-title text-5xl font-bold mb-4">
              <span className="gradient-text">Ready to Move Homes</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Modern homes designed for comfortable living with world-class amenities
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredHomes.map((home, index) => (
              <Card 
                key={home.id} 
                className="property-card group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="property-image-wrapper relative overflow-hidden">
                  <img 
                    src={home.image} 
                    alt={home.title}
                    className="property-image w-full h-64 object-cover"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-orange-500 text-white border-0">{home.status}</Badge>
                  </div>
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-black/80 text-orange-400 border-orange-500/50">
                      <HomeIcon className="w-3 h-3 mr-1" />
                      Home
                    </Badge>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white group-hover:text-orange-400 transition-colors">
                    {home.title}
                  </h3>
                  
                  <div className="flex items-center text-gray-400 text-sm mb-4">
                    <MapPin className="w-4 h-4 mr-1" />
                    {home.location}
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="plot-detail">
                      <div className="text-xs text-gray-500">Bedrooms</div>
                      <div className="text-sm font-semibold text-white">{home.bedrooms} BHK</div>
                    </div>
                    <div className="plot-detail">
                      <div className="text-xs text-gray-500">Area</div>
                      <div className="text-sm font-semibold text-white">{home.size}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                    <div>
                      <div className="text-2xl font-bold gradient-text">{home.price}</div>
                    </div>
                    <Link to="/properties">
                      <Button size="sm" className="cta-button-mini">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/properties">
              <Button size="lg" className="cta-button-primary group">
                View All Properties
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50">
              Our Services
            </Badge>
            <h2 className="section-title text-5xl font-bold mb-4">
              <span className="gradient-text">What We Offer</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = {
                MapPin, Home: HomeIcon, Trees: MapPin, Store: HomeIcon, 
                FileCheck: CheckCircle, Settings: Shield
              }[service.icon] || CheckCircle;

              return (
                <Card 
                  key={index} 
                  className="service-card group p-8"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="service-icon-wrapper mb-6">
                    <IconComponent className="w-8 h-8 text-orange-400" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-orange-400 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {service.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50">
              Why Choose Us
            </Badge>
            <h2 className="section-title text-5xl font-bold mb-4">
              <span className="gradient-text">Your Trusted Partner</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="feature-card text-center">
              <div className="feature-icon mb-4">
                <TrendingUp className="w-12 h-12" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Best Investment</h3>
              <p className="text-gray-400">Prime locations with high appreciation potential</p>
            </div>
            
            <div className="feature-card text-center">
              <div className="feature-icon mb-4">
                <Shield className="w-12 h-12" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Verified Properties</h3>
              <p className="text-gray-400">100% genuine listings with clear documentation</p>
            </div>
            
            <div className="feature-card text-center">
              <div className="feature-icon mb-4">
                <CheckCircle className="w-12 h-12" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Legal Support</h3>
              <p className="text-gray-400">Complete legal assistance for hassle-free transactions</p>
            </div>
            
            <div className="feature-card text-center">
              <div className="feature-icon mb-4">
                <Clock className="w-12 h-12" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">10+ Years Experience</h3>
              <p className="text-gray-400">Trusted by thousands of satisfied customers</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50">
              Testimonials
            </Badge>
            <h2 className="section-title text-5xl font-bold mb-4">
              <span className="gradient-text">What Our Clients Say</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial-card p-8">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-orange-400 fill-orange-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 italic">"{testimonial.text}"</p>
                <div className="border-t border-gray-800 pt-4">
                  <div className="font-bold text-white">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-20 relative overflow-hidden">
        <div className="cta-gradient absolute inset-0"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-5xl font-bold mb-6 text-white">
              Ready to Find Your Dream Property?
            </h2>
            <p className="text-xl text-gray-300 mb-10">
              Contact our expert team today and let us help you make the right investment decision
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                size="lg" 
                className="cta-button-primary group"
                onClick={handleWhatsApp}
              >
                <MessageCircle className="mr-2 w-5 h-5" />
                Chat on WhatsApp
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              <Button 
                size="lg" 
                variant="outline"
                className="cta-button-secondary"
                onClick={() => handleCall(companyInfo.phones[0])}
              >
                <Phone className="mr-2 w-5 h-5" />
                Call Now
              </Button>
            </div>

            <div className="mt-12 flex flex-wrap justify-center gap-8 text-center">
              {companyInfo.phones.map((phone, index) => (
                <div key={index} className="contact-badge">
                  <Phone className="w-5 h-5 mb-2 mx-auto text-orange-400" />
                  <div className="text-white font-semibold text-lg">+91 {phone}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;